import RestClient from "@leanspace/js-client/dist/RestClient";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {useTenants} from "Hooks/tenants";
import React, {useEffect, useMemo, useState} from "react";
import {DownloadCloud} from "react-feather";

import LicensesClient from "../../Services/Licenses/LicensesClient";
import {License} from "../../Types/licenses";
import {formatDate, formatDateTime, formatJwt, getToken} from "../../utils";

const DATE_COLUMNS = new Set<keyof License>([
  "validFrom",
  "validTo",
  "graceUntil",
]);

const DATETIME_COLUMNS = new Set<keyof License>([
  "createdAt",
]);

const columns: { id: keyof License; label: string }[] = [
  {id: "name", label: "Name"},
  {id: "tenant", label: "Tenant"},
  {id: "validFrom", label: "Valid From"},
  {id: "validTo", label: "Valid To"},
  {id: "graceUntil", label: "Grace Until"},
  {id: "createdAt", label: "Created At"},
];

const Licenses: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [query, setQuery] = useState<string>("");
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState<keyof License>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [downloadedValue, setDownloadedValue] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState<License[]>([]);
  const [deletePermanentTargets, setDeletePermanentTargets] = useState<License[]>([]);
  const [deleteSoftTargets, setDeleteSoftTargets] = useState<License[]>([]);
  const [deleteAcknowledgeRequired, setDeleteAcknowledgeRequired] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const {data: tenantsData, refetch: refetchTenants} = useTenants() as any;
  const tenants = tenantsData?.content ?? [];

  useEffect(() => {
    // ensure tenant list is fresh when opening the dialog
    if (createOpen && refetchTenants) {
      refetchTenants();
    }
  }, [createOpen, refetchTenants]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleDetails = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const today = dayjs().startOf("day");
  const [createForm, setCreateForm] = useState<{
    name: string;
    description: string;
    tenant: string;
    validFrom: dayjs.Dayjs;
    validTo: dayjs.Dayjs;
    graceUntil: dayjs.Dayjs;
    services: string[];
  }>({
    name: "",
    description: "",
    tenant: "",
    validFrom: today,
    validTo: today.add(12, "month"),
    graceUntil: today.add(13, "month"),
    services: [],
  });

  const env = localStorage.getItem("env") as "DEV" | "DEMO" | "PROD" | "PROM001";
  const restClient = useMemo(() => {
    return new RestClient({
      getToken,
      baseURL: `https://licenses-${["DEV", "DEMO"].includes(env) ? "develop" : "prod"}.agnostic.leanspace.io`,
    });
  }, []);

  const licensesClient = useMemo(() => {
    return new LicensesClient(restClient);
  }, [restClient]);

  const load = async () => {
    const result = await licensesClient.searchLicenses({
      query,
      includeDeleted,
      page,
      size,
      sort: `${String(orderBy)},${order}`,
    } as any);

    setLicenses(result.content);
    setTotal(result.totalElements);
  };

  useEffect(() => {
    load();
  }, [query, includeDeleted, page, size, orderBy, order]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelected(licenses.map((l) => l.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSort = (property: keyof License) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDownload = async (id: string) => {
    const result = await licensesClient.downloadLicense(id);
    setDownloadedValue(result.value);
  };

  const handleDelete = async () => {
    const selectedLicenses = licenses.filter((l) => selected.includes(l.id));

    const alreadyDeleted = selectedLicenses.filter((l) => l.deleted);
    const notDeleted = selectedLicenses.filter((l) => !l.deleted);

    // Always show confirmation.
    setDeleteTargets(selectedLicenses);
    setDeletePermanentTargets(alreadyDeleted);
    setDeleteSoftTargets(notDeleted);
    setDeleteAcknowledgeRequired(alreadyDeleted.length > 0);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    // If any selected license is already soft-deleted, we require acknowledgement and pass acknowledged=true.
    // Backend should treat this as permanent deletion for already-deleted items and soft deletion for the rest.
    if (deleteAcknowledgeRequired) {
      await Promise.all(selected.map((id) => licensesClient.deleteLicense(id, true)));
    } else {
      await Promise.all(deleteTargets.map((l) => licensesClient.deleteLicense(l.id)));
    }

    setDeleteConfirmOpen(false);
    setDeleteTargets([]);
    setDeletePermanentTargets([]);
    setDeleteSoftTargets([]);
    setDeleteAcknowledgeRequired(false);
    setSelected([]);
    load();
  };

  const handleCreate = async () => {
    await licensesClient.createLicense({
      name: createForm.name,
      description: createForm.description || undefined,
      tenant: createForm.tenant,
      validFrom: createForm.validFrom.utc().startOf("day").toISOString(),
      validTo: createForm.validTo.utc().startOf("day").toISOString(),
      graceUntil: createForm.graceUntil.utc().startOf("day").toISOString(),
      rules: {
        services: createForm.services,
      },
    });

    setCreateOpen(false);
    const resetToday = dayjs().startOf("day");
    setCreateForm({
      name: "",
      description: "",
      tenant: "",
      validFrom: resetToday,
      validTo: resetToday.add(12, "month"),
      graceUntil: resetToday.add(13, "month"),
      services: [],
    });

    load();
  };

  return (
    <Box sx={{padding: 2}}>
      <Toolbar sx={{display: "flex", justifyContent: "space-between"}}>
        <Box sx={{display: "flex", gap: 2}}>
          <TextField
            label="Search"
            value={query}
            onChange={(e) => {
              setPage(0);
              setQuery(e.target.value);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={includeDeleted}
                onChange={(e) => setIncludeDeleted(e.target.checked)}
              />
            }
            label="Show deleted"
          />
        </Box>
        <Box sx={{display: "flex", gap: 2}}>
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={selected.length === 0}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>
      </Toolbar>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      licenses.length > 0 &&
                      selected.length === licenses.length
                    }
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < licenses.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {licenses.map((license) => (
                <React.Fragment key={license.id}>
                  <TableRow
                    hover
                    sx={
                      license.deleted
                        ? {
                          backgroundColor: (theme) => theme.palette.action.hover,
                          opacity: 0.75,
                        }
                        : undefined
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(license.id)}
                        onChange={() => handleSelect(license.id)}
                      />
                    </TableCell>
                    {columns.map((column) => {
                      const value = license[column.id] as any;

                      if (DATE_COLUMNS.has(column.id)) {
                        return (
                          <TableCell key={column.id}>
                            {formatDate(value)}
                          </TableCell>
                        );
                      }

                      if (DATETIME_COLUMNS.has(column.id)) {
                        return (
                          <TableCell key={column.id}>
                            {formatDateTime(value)}
                          </TableCell>
                        );
                      }

                      if (column.id === "name") {
                        return (
                          <TableCell key={column.id}>
                            <Box sx={{display: "flex", flexDirection: "column"}}>
                              <Typography>{value}</Typography>
                              {license.deleted && (
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{fontWeight: 500}}
                                >
                                  Deleted
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        );
                      }

                      return <TableCell key={column.id}>{value}</TableCell>;
                    })}
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => toggleDetails(license.id)}
                      >
                        {expanded.has(license.id) ? "Hide details" : "See details"}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow key={`${license.id}-details`}>
                    <TableCell
                      colSpan={columns.length + 2}
                      sx={{paddingBottom: 0, paddingTop: 0, borderBottom: 0}}
                    >
                      <Collapse in={expanded.has(license.id)} timeout="auto" unmountOnExit>
                        <Box sx={{padding: 2}}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              mb: 2,
                            }}
                          >
                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                              License details
                            </Typography>

                            <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => toggleDetails(license.id)}
                              >
                                Hide details
                              </Button>

                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleDownload(license.id)}
                                disabled={license.deleted}
                                startIcon={<DownloadCloud/>}
                              >
                                Download license
                              </Button>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"},
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                ID
                              </Typography>
                              <Typography sx={{wordBreak: "break-word"}}>
                                {license.id}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Status
                              </Typography>
                              <Typography>
                                {license.deleted ? "Deleted (soft)" : "Active"}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Name
                              </Typography>
                              <Typography>{license.name}</Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Tenant
                              </Typography>
                              <Typography>{license.tenant}</Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Valid from
                              </Typography>
                              <Typography>{formatDate((license as any).validFrom)}</Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Valid to
                              </Typography>
                              <Typography>{formatDate((license as any).validTo)}</Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Grace until
                              </Typography>
                              <Typography>{formatDate((license as any).graceUntil)}</Typography>
                            </Box>

                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Created at
                              </Typography>
                              <Typography>{formatDateTime((license as any).createdAt)}</Typography>
                            </Box>

                            <Box sx={{gridColumn: {xs: "1", sm: "1 / span 2"}}}>
                              <Typography variant="caption" color="text.secondary">
                                Description
                              </Typography>
                              <Typography>
                                {(license as any).description || "—"}
                              </Typography>
                            </Box>

                            <Box sx={{gridColumn: {xs: "1", sm: "1 / span 2"}}}>
                              <Typography variant="caption" color="text.secondary">
                                Rules
                              </Typography>

                              <Box sx={{mt: 1}}>
                                <Typography variant="caption" color="text.secondary">
                                  Services
                                </Typography>

                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5}}>
                                  {(((license as any).rules?.services ?? []) as string[]).length === 0 ? (
                                    <Typography>—</Typography>
                                  ) : (
                                    (((license as any).rules?.services ?? []) as string[]).map((service) => (
                                      <Chip key={service} label={service} size="small"/>
                                    ))
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => {
            setSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {deleteAcknowledgeRequired ? "Permanent Deletion Warning" : "Confirm deletion"}
        </DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: 1}}>
          {deleteAcknowledgeRequired ? (
            <>
              <Typography color="error" fontWeight="bold">
                The following licenses will be permanently deleted:
              </Typography>

              {deletePermanentTargets.map((license) => (
                <Typography key={license.id}>
                  • {license.name} ({license.tenant})
                </Typography>
              ))}

              <Typography sx={{mt: 2}}>
                Licenses not listed above will be soft deleted.
              </Typography>

              {deleteSoftTargets.length > 0 && (
                <Box sx={{mt: 1}}>
                  {deleteSoftTargets.map((license) => (
                    <Typography key={license.id}>
                      • {license.name} ({license.tenant})
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography>
                You are about to soft delete the following licenses:
              </Typography>

              {deleteTargets.map((license) => (
                <Typography key={license.id}>
                  • {license.name} ({license.tenant})
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setDeleteTargets([]);
              setDeletePermanentTargets([]);
              setDeleteSoftTargets([]);
              setDeleteAcknowledgeRequired(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={deleteAcknowledgeRequired ? "error" : "primary"}
            onClick={handleConfirmDelete}
          >
            {deleteAcknowledgeRequired ? "Confirm Permanent Delete" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create License</DialogTitle>
        <DialogContent sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
          <TextField
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
            fullWidth
          />
          <TextField
            label="Description"
            value={createForm.description}
            onChange={(e) => setCreateForm({...createForm, description: e.target.value})}
            fullWidth
          />
          <Autocomplete
            options={tenants}
            value={
              tenants.find((t: any) => String(t.name) === String(createForm.tenant)) ?? null
            }
            onChange={(_, value) => {
              setCreateForm({
                ...createForm,
                tenant: value ? String((value as any).name) : "",
              });
            }}
            getOptionLabel={(option: any) => option?.name ?? ""}
            isOptionEqualToValue={(option: any, value: any) => option?.name === value?.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tenant"
                placeholder="Select tenant"
                fullWidth
              />
            )}
          />
          <DatePicker<dayjs.Dayjs>
            inputFormat="DD MMM YYYY"
            label="Valid From"
            value={createForm.validFrom}
            onChange={(value: dayjs.Dayjs | null) => {
              if (!value) return;
              setCreateForm({...createForm, validFrom: value.startOf("day")});
            }}
            renderInput={(params) => <TextField {...params} fullWidth/>}
          />
          <DatePicker<dayjs.Dayjs>
            inputFormat="DD MMM YYYY"
            label="Valid To"
            value={createForm.validTo}
            onChange={(value: dayjs.Dayjs | null) => {
              if (!value) return;
              setCreateForm({...createForm, validTo: value.startOf("day")});
            }}
            renderInput={(params) => <TextField {...params} fullWidth/>}
          />
          <DatePicker<dayjs.Dayjs>
            inputFormat="DD MMM YYYY"
            label="Grace Until"
            value={createForm.graceUntil}
            onChange={(value: dayjs.Dayjs | null) => {
              if (!value) return;
              setCreateForm({...createForm, graceUntil: value.startOf("day")});
            }}
            renderInput={(params) => <TextField {...params} fullWidth/>}
          />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={createForm.services}
            onChange={(_, value) => {
              const normalized = (value as string[])
                .map((v) => v.trim())
                .filter(Boolean);

              // de-duplicate (case-insensitive) while keeping original casing of first occurrence
              const seen = new Set<string>();
              const deduped: string[] = [];
              for (const v of normalized) {
                const key = v.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                deduped.push(v);
              }

              setCreateForm({...createForm, services: deduped});
            }}
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip
                  variant="outlined"
                  label={option}
                  size="small"
                  {...getTagProps({index})}
                  key={`${option}-${index}`}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Services"
                placeholder="Type and press Enter"
                fullWidth
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!downloadedValue}
        onClose={() => setDownloadedValue(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>License</DialogTitle>
        <DialogContent>
          {downloadedValue ? (() => {
            const formatted = formatJwt(downloadedValue);

            // Fallback for non-JWT / unexpected format
            if (!formatted.isJwt) {
              return (
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    backgroundColor: "action.hover",
                    padding: 2,
                    borderRadius: 1,
                    margin: 0,
                  }}
                >
                  {formatted.raw}
                </Box>
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    backgroundColor: "action.hover",
                    padding: 2,
                    borderRadius: 1,
                    margin: 0,
                  }}
                >
                  <Box sx={{color: "primary.main", fontWeight: 700, mb: 1}}>
                    HEADER
                  </Box>
                  {formatted.header ?? "—"}
                </Box>

                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    backgroundColor: "action.hover",
                    padding: 2,
                    borderRadius: 1,
                    margin: 0,
                  }}
                >
                  <Box sx={{color: "success.main", fontWeight: 700, mb: 1}}>
                    PAYLOAD
                  </Box>
                  {formatted.payload ?? "—"}
                </Box>

                <Box
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                    backgroundColor: "action.hover",
                    padding: 2,
                    borderRadius: 1,
                    margin: 0,
                  }}
                >
                  <Box sx={{color: "text.secondary", fontWeight: 700, mb: 1}}>
                    SIGNATURE
                  </Box>
                  {formatted.signature ?? "—"}
                </Box>
              </Box>
            );
          })() : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (downloadedValue) {
                navigator.clipboard.writeText(downloadedValue);
              }
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setDownloadedValue(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Licenses;
